import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Layers } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ComingSoonPlaceholder } from "@/components/video-library/ComingSoonPlaceholder";
import { NEWS_CATEGORY_SLUG } from "@/lib/newsCategory";

import digitalTransformation from "@/assets/categories/digital-transformation.png";
import technologyProgramming from "@/assets/categories/technology-programming.png";
import architectureInteriorDesign from "@/assets/categories/architecture-interior-design.png";
import marketingAdvertising from "@/assets/categories/marketing-advertising.png";
import medicineHealth from "@/assets/categories/medicine-health.png";
import designVideo from "@/assets/categories/design-video.png";
import humanResourcesWorkEthic from "@/assets/categories/human-resources-work-ethic.png";
import legalProcedures from "@/assets/categories/legal-procedures.png";
import kidsTeens from "@/assets/categories/kids-teens.png";
import accountingAdministration from "@/assets/categories/accounting-and-administration.png";

const FALLBACK_ICONS: Record<string, string> = {
  "digital-transformation": digitalTransformation,
  "technology-programming": technologyProgramming,
  "architecture-interior-design": architectureInteriorDesign,
  "marketing-advertising": marketingAdvertising,
  "medicine-health": medicineHealth,
  "design-video": designVideo,
  "human-resources-work-ethic": humanResourcesWorkEthic,
  "legal-procedures": legalProcedures,
  "kids-teens": kidsTeens,
  "accounting-and-administration": accountingAdministration,
};

interface CategoryItem {
  id: string;
  slug: string;
  name: string;
  name_es: string | null;
  square_image_url: string | null;
  display_order: number;
  landing_page_enabled: boolean;
  parent_id: string | null;
  hasVideos: boolean;
}

const useMenuCategories = () =>
  useQuery({
    queryKey: ["nav-mega-categories"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CategoryItem[]> => {
      const [catsRes, linksRes] = await Promise.all([
        supabase
          .from("video_categories")
          .select("id, slug, name, name_es, square_image_url, display_order, landing_page_enabled, parent_id")
          .order("display_order", { ascending: true }),
        supabase
          .from("video_library")
          .select("video_library_categories(category_id), video_price_tiers(access_type,price)")
          .eq("is_published", true),
      ]);
      if (catsRes.error) throw catsRes.error;
      const active = new Set<string>();
      let hasFreeVideo = false;
      (linksRes.data || []).forEach((v: any) => {
        (v.video_library_categories || []).forEach((l: any) => {
          if (l.category_id) active.add(l.category_id);
        });
        const tier = v.video_price_tiers;
        if (tier?.access_type === "free" || Number(tier?.price ?? 0) === 0) {
          hasFreeVideo = true;
        }
      });
      return (catsRes.data || [])
        .filter((c: any) => c.slug !== NEWS_CATEGORY_SLUG)
        .map((c: any) => ({
          id: c.id,
          slug: c.slug,
          name: c.name,
          name_es: c.name_es,
          square_image_url: c.square_image_url,
          display_order: c.display_order ?? 0,
          landing_page_enabled: c.landing_page_enabled ?? true,
          parent_id: c.parent_id ?? null,
          hasVideos: active.has(c.id) || (c.slug === "free" && hasFreeVideo),
        }));
    },
  });

interface Props {
  onNavigate: (path: string) => void;
  basePath?: string;
}

export const VideoLibraryMegaMenu = ({ onNavigate, basePath = "/videos" }: Props) => {
  const { t, i18n } = useTranslation("landing");
  const isEs = i18n.language?.toLowerCase().startsWith("es");
  const { data: categories, isLoading } = useMenuCategories();
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  const handleClick = (cat: CategoryItem) => {
    if (!cat.hasVideos) {
      setComingSoonOpen(true);
      return;
    }
    // "Free" and any category with the landing page disabled jump into
    // the filtered library view instead of the /category/:slug page.
    if (cat.slug === "free" || !cat.landing_page_enabled) {
      onNavigate(`${basePath}/category/${cat.slug}`);
    } else {
      onNavigate(`/category/${cat.slug}`);
    }
  };


  return (
    <div className="w-full lg:w-[640px] lg:max-w-[640px] bg-transparent lg:bg-background border-0 lg:border lg:border-border rounded-none lg:rounded-xl shadow-none lg:shadow-2xl overflow-hidden">
      <div className="px-0 lg:px-5 py-3 lg:border-b border-border bg-transparent lg:bg-muted/30 flex items-center justify-between">
        <button
          onClick={() => onNavigate("/categories")}
          aria-label={t("nav.categories", "Categories")}
          className="group inline-flex items-center gap-2 -mx-2 px-2 py-1.5 rounded-md hover:bg-primary/10 transition-colors"
        >
          <Layers className="w-5 h-5 text-primary" />
          <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground group-hover:text-primary transition-colors">
            {t("nav.categories", "Categories")}
          </p>
        </button>
        <button
          onClick={() => onNavigate(basePath)}

          className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          {t("nav.see_full_library", "See full library")}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="p-0 lg:p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
        {isLoading
          ? Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg animate-pulse">
                <div className="w-10 h-10 rounded bg-muted shrink-0" />
                <div className="h-3 bg-muted rounded flex-1" />
              </div>
            ))
          : (() => {
              const all = categories || [];
              const subsByParent = new Map<string, CategoryItem[]>();
              all.forEach((c) => {
                if (c.parent_id) {
                  const arr = subsByParent.get(c.parent_id) ?? [];
                  arr.push(c);
                  subsByParent.set(c.parent_id, arr);
                }
              });
              const mains = all.filter((c) => !c.parent_id || !all.some((p) => p.id === c.parent_id));

              const renderButton = (cat: CategoryItem, small = false) => {
                const label = (isEs && cat.name_es) || cat.name;
                const img = cat.square_image_url || FALLBACK_ICONS[cat.slug];
                const muted = !cat.hasVideos;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleClick(cat)}
                    className={`flex items-center gap-2 ${small ? "px-2 py-1.5" : "p-2"} rounded-lg text-left transition-colors group w-full ${
                      muted ? "opacity-60 hover:opacity-100 hover:bg-muted/80" : "hover:bg-input"
                    }`}
                    aria-label={label}
                  >
                    <div
                      className={`${small ? "w-7 h-7" : "w-10 h-10"} rounded bg-white shrink-0 flex items-center justify-center overflow-hidden ring-1 ring-border`}
                    >
                      {img ? (
                        <img
                          src={img}
                          alt=""
                          loading="lazy"
                          className={small ? "w-6 h-6 object-contain" : "w-9 h-9 object-contain"}
                        />
                      ) : (
                        <span className="text-primary font-bold text-sm">{label.charAt(0)}</span>
                      )}
                    </div>
                    <span
                      className={`${small ? "text-xs" : "text-sm font-medium"} text-foreground line-clamp-2 group-hover:text-primary transition-colors`}
                    >
                      {label}
                    </span>
                  </button>
                );
              };

              return mains.map((cat) => {
                const subs = subsByParent.get(cat.id) ?? [];
                if (!subs.length) return renderButton(cat);
                return (
                  <div key={cat.id} className="rounded-xl bg-muted p-1">
                    {renderButton(cat)}
                    <div className="pl-2 pb-1 space-y-0.5">{subs.map((s) => renderButton(s, true))}</div>
                  </div>
                );
              });
            })()}
      </div>


      <div className="hidden lg:block px-0 lg:px-5 py-3 lg:border-t border-border bg-transparent lg:bg-muted/20">
        <button
          onClick={() => onNavigate(basePath)}
          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          {t("nav.browse_all_videos", "Browse all videos")}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <Dialog open={comingSoonOpen} onOpenChange={setComingSoonOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-0">
          <DialogTitle className="sr-only">
            {t("coming_soon.title", "Coming Soon")}
          </DialogTitle>
          <div className="relative aspect-video w-full">
            <ComingSoonPlaceholder />
          </div>
          <div className="px-6 py-4 text-center bg-black">
            <p className="text-white text-base md:text-lg font-medium">
              {t(
                "categories.coming_soon_message",
                "We are working on this category, videos coming soon!",
              )}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoLibraryMegaMenu;
